import { motion, useInView } from 'motion/react';
import { useRef } from 'react';
import Link from 'next/link';
import { ArrowRight, Briefcase } from 'lucide-react';
import { CAREERS_COPY } from '../../../constants/careersTranslations';

export function CareersOpenPositionsSection({
  lang = 'en',
  positions = null,
  copy,
  fallbackUrl,
  fetchFailed = false,
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const copy_text = copy?.openPositions ?? CAREERS_COPY[lang]?.openPositions ?? CAREERS_COPY.en.openPositions;
  const list = positions?.length > 0 ? positions : [];

  // Fallback: no positions to show. Render a single card linking to Hailey careers.
  const isEmpty = !list || list.length === 0;
  const fallbackLabel = lang === 'fi'
    ? 'Katso kaikki avoimet paikat'
    : 'See all open positions';

  return (
    <section id="open-positions" ref={ref} className="section-rhythm bg-navy text-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-5 pointer-events-none" aria-hidden>
        <div className="absolute top-0 right-0 w-96 h-96 bg-highlight rounded-full blur-3xl" />
      </div>
      <div className="max-w-4xl mx-auto px-6 relative z-10">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="heading-2 mb-4 text-white"
        >
          {copy_text.title}
        </motion.h2>
        {copy_text.noJobs ? (
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="text-lg text-gray-300 mb-8"
          >
            {copy_text.noJobs}
          </motion.p>
        ) : null}
        {copy_text.note ? (
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-sm text-gray-400 mb-8"
          >
            {copy_text.note}
          </motion.p>
        ) : null}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {isEmpty && fetchFailed && fallbackUrl ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="min-h-0 sm:col-span-2"
            >
              <Link
                href={fallbackUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col p-6 w-fit mt-4 rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 hover:-translate-y-1 transition-all duration-200 cursor-pointer group focus:outline-none focus-visible:ring-2 focus-visible:ring-highlight focus-visible:ring-offset-2 focus-visible:ring-offset-[#0f172a] touch-manipulation"
              >
                <div className="">
                <h3 className="font-bold text-white mb-0.5">{fallbackLabel}</h3>
                </div>
              </Link>
            </motion.div>
          ) : (
            list.map((job, index) => {
              const title = lang === 'fi' ? (job.titleFi ?? job.title) : job.title;
              const location = lang === 'fi' ? (job.locationFi ?? job.location) : job.location;
              const applyUrl = lang === 'fi' ? (job.applyUrl ?? job.applyUrlEn ?? '#') : (job.applyUrlEn ?? job.applyUrl ?? '#');
              return (
                <motion.div
                  key={job.id ?? `${job.title}-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.15 + index * 0.08 }}
                  className="min-h-0"
                >
                  <Link
                    href={applyUrl}
                    className="flex flex-col h-full min-h-[140px] p-6 rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 hover:-translate-y-1 transition-all duration-200 cursor-pointer group focus:outline-none focus-visible:ring-2 focus-visible:ring-highlight focus-visible:ring-offset-2 focus-visible:ring-offset-[#0f172a] touch-manipulation"
                  >
                    <div className="flex items-start gap-4 flex-1 min-w-0">
                      <div className="w-12 h-12 rounded-xl bg-highlight/20 flex items-center justify-center shrink-0 border border-highlight/30">
                        <Briefcase className="w-6 h-6 text-highlight" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-bold text-white mb-0.5">{title}</h3>
                        {location ? <p className="text-sm text-gray-400">{location}</p> : null}
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-2 text-highlight font-semibold shrink-0 mt-4 group-hover:gap-3 transition-all group-hover:text-white">
                      {copy_text.apply}
                      <ArrowRight className="w-4 h-4 shrink-0" />
                    </span>
                  </Link>
                </motion.div>
              );
            })
          )}
        </div>
      </div>
    </section>
  );
}
